import React, { useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useFormContext } from 'react-hook-form';

import BWAccordion from 'components/shared/BWAccordion/index';
import DataTable from 'components/shared/DataTable';
import FormInput from 'components/shared/BWFormControl/FormInput';
import FormNumber from 'components/shared/BWFormControl/FormNumber';
import ProcurementHintBox from 'components/shared/ProcurementHintBox';

import ICON_COMMON from 'utils/icons.common';
import { showConfirmModal } from 'actions/global';
import FormCurrency from 'pages/DiscountProgram/components/shared/FormCurrency';
import { CurrencyType, ProcurementDiscountTypeCode, ProcurementDiscountTypeOptions } from 'pages/DiscountProgram/ultils/constant';

const SINGLE_RULE_TYPES = [
  ProcurementDiscountTypeCode.DIRECT_PERCENT,
  ProcurementDiscountTypeCode.DIRECT_AMOUNT_ORDER,
  ProcurementDiscountTypeCode.DIRECT_AMOUNT_PER_UNIT,
];

const RULE_TYPE_HINTS = {
  [ProcurementDiscountTypeCode.DIRECT_PERCENT]:
    'Áp một mức % cố định cho toàn bộ báo giá/đơn mua. Phù hợp case NCC báo chiết khấu trực tiếp trên invoice.',
  [ProcurementDiscountTypeCode.DIRECT_AMOUNT_ORDER]:
    'Áp một số tiền cố định cho cả đơn/hồ sơ báo giá. Hệ thống sẽ phân bổ theo tỷ trọng giá trị dòng hàng khi tính giá sau chiết khấu.',
  [ProcurementDiscountTypeCode.DIRECT_AMOUNT_PER_UNIT]:
    'Áp số tiền chiết khấu cố định trên mỗi đơn vị hàng mua. Thường dùng cho hỗ trợ hãng/NCC theo máy hoặc phụ kiện.',
  [ProcurementDiscountTypeCode.TIER_BY_QTY]:
    'Áp chiết khấu theo bậc số lượng từng dòng hàng. Buyer nhập SL dự kiến đặt, hệ thống tự chọn bậc phù hợp.',
  [ProcurementDiscountTypeCode.TIER_BY_ORDER_VALUE]:
    'Áp chiết khấu theo bậc giá trị toàn đơn/hồ sơ. Phù hợp case NCC chốt % hoặc số tiền theo tổng giá trị đơn hàng.',
  [ProcurementDiscountTypeCode.PERIOD_REBATE]:
    'Áp hoàn chiết khấu / hỗ trợ sau báo cáo hoặc cuối kỳ. Dùng để theo dõi khoản được hưởng sau khi đạt điều kiện và làm căn cứ quyết toán sau.',
};

const createDefaultRule = (typeCode, sortOrder = 1) => {
  const baseRule = {
    sort_order: sortOrder,
    min_quantity: null,
    max_quantity: null,
    min_order_value: null,
    max_order_value: null,
    value_type: CurrencyType.PERCENT,
    value: 0,
    note: '',
  };

  if (typeCode === ProcurementDiscountTypeCode.DIRECT_PERCENT) {
    return { ...baseRule, value_type: CurrencyType.PERCENT };
  }

  if (
    typeCode === ProcurementDiscountTypeCode.DIRECT_AMOUNT_ORDER ||
    typeCode === ProcurementDiscountTypeCode.DIRECT_AMOUNT_PER_UNIT
  ) {
    return { ...baseRule, value_type: CurrencyType.MONEY };
  }

  return baseRule;
};

const DiscountProgramCondition = ({ disabled, loading }) => {
  const methods = useFormContext();
  const { watch, setValue, getValues } = methods;
  const dispatch = useDispatch();
  const previousTypeRef = useRef(null);

  const procurementDiscountTypeCode = watch('procurement_discount_type_code');
  const procurementRuleList = watch('procurement_rule_list') || [];

  useEffect(() => {
    if (!procurementDiscountTypeCode) {
      setValue('procurement_rule_list', []);
      previousTypeRef.current = null;
      return;
    }

    const previousType = previousTypeRef.current;
    const currentRules = getValues('procurement_rule_list') || [];
    const shouldResetRules = previousType && previousType !== procurementDiscountTypeCode;
    const nextRules = shouldResetRules
      ? [createDefaultRule(procurementDiscountTypeCode, 1)]
      : currentRules.length
      ? currentRules.map((rule, index) => ({
          ...createDefaultRule(procurementDiscountTypeCode, index + 1),
          ...rule,
          sort_order: index + 1,
          value_type:
            procurementDiscountTypeCode === ProcurementDiscountTypeCode.DIRECT_PERCENT
              ? CurrencyType.PERCENT
              : procurementDiscountTypeCode === ProcurementDiscountTypeCode.DIRECT_AMOUNT_ORDER ||
                procurementDiscountTypeCode === ProcurementDiscountTypeCode.DIRECT_AMOUNT_PER_UNIT
              ? CurrencyType.MONEY
              : rule?.value_type || CurrencyType.PERCENT,
        }))
      : [createDefaultRule(procurementDiscountTypeCode, 1)];

    const normalizedRules = SINGLE_RULE_TYPES.includes(procurementDiscountTypeCode) ? [nextRules[0]] : nextRules;
    setValue('procurement_rule_list', normalizedRules);
    previousTypeRef.current = procurementDiscountTypeCode;
  }, [getValues, procurementDiscountTypeCode, setValue]);

  const handleAddRule = () => {
    const nextRules = [...procurementRuleList, createDefaultRule(procurementDiscountTypeCode, procurementRuleList.length + 1)];
    setValue('procurement_rule_list', nextRules);
  };

  const handleDeleteRule = (index) => {
    dispatch(
      showConfirmModal(['Xóa dòng điều kiện chiết khấu này?'], () => {
        const nextRules = _.cloneDeep(procurementRuleList);
        nextRules.splice(index, 1);
        setValue(
          'procurement_rule_list',
          nextRules.length
            ? nextRules.map((rule, idx) => ({ ...rule, sort_order: idx + 1 }))
            : [createDefaultRule(procurementDiscountTypeCode, 1)],
        );
      }),
    );
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        header: 'STT',
        classNameHeader: 'bw_text_center',
        classNameBody: 'bw_text_center',
        formatter: (_, index) => index + 1,
      },
    ];

    if (procurementDiscountTypeCode === ProcurementDiscountTypeCode.TIER_BY_QTY) {
      baseColumns.push(
        {
          header: 'SL từ',
          classNameHeader: 'bw_text_center',
          formatter: (_, index) => (
            <FormNumber
              bordered
              min={0}
              field={`procurement_rule_list.[${index}].min_quantity`}
              disabled={disabled}
              validation={{
                required: 'SL từ là bắt buộc',
                min: {
                  value: 0.000001,
                  message: 'SL từ phải lớn hơn 0',
                },
              }}
            />
          ),
        },
        {
          header: 'SL đến',
          classNameHeader: 'bw_text_center',
          formatter: (_, index) => (
            <FormNumber bordered min={0} field={`procurement_rule_list.[${index}].max_quantity`} disabled={disabled} />
          ),
        },
      );
    }

    if (
      procurementDiscountTypeCode === ProcurementDiscountTypeCode.TIER_BY_ORDER_VALUE ||
      procurementDiscountTypeCode === ProcurementDiscountTypeCode.PERIOD_REBATE
    ) {
      baseColumns.push(
        {
          header: 'Giá trị đơn từ',
          classNameHeader: 'bw_text_center',
          formatter: (_, index) => (
            <FormNumber
              bordered
              min={0}
              field={`procurement_rule_list.[${index}].min_order_value`}
              disabled={disabled}
              validation={{
                required: 'Giá trị đơn từ là bắt buộc',
                min: {
                  value: 0,
                  message: 'Giá trị đơn từ không hợp lệ',
                },
              }}
            />
          ),
        },
        {
          header: 'Giá trị đơn đến',
          classNameHeader: 'bw_text_center',
          formatter: (_, index) => (
            <FormNumber bordered min={0} field={`procurement_rule_list.[${index}].max_order_value`} disabled={disabled} />
          ),
        },
      );
    }

    baseColumns.push({
      header: 'Giá trị chiết khấu',
      classNameHeader: 'bw_text_center',
      formatter: (_, index) => {
        if (procurementDiscountTypeCode === ProcurementDiscountTypeCode.DIRECT_PERCENT) {
          return (
            <FormNumber
              bordered
              min={0}
              max={100}
              addonAfter='%'
              field={`procurement_rule_list.[${index}].value`}
              disabled={disabled}
              validation={{
                required: 'Mức chiết khấu là bắt buộc',
              }}
            />
          );
        }

        if (
          procurementDiscountTypeCode === ProcurementDiscountTypeCode.DIRECT_AMOUNT_ORDER ||
          procurementDiscountTypeCode === ProcurementDiscountTypeCode.DIRECT_AMOUNT_PER_UNIT
        ) {
          return (
            <FormNumber
              bordered
              min={0}
              field={`procurement_rule_list.[${index}].value`}
              disabled={disabled}
              validation={{
                required: 'Số tiền chiết khấu là bắt buộc',
              }}
            />
          );
        }

        return (
          <FormCurrency
            fieldType={`procurement_rule_list.[${index}].value_type`}
            fieldValue={`procurement_rule_list.[${index}].value`}
            disabled={disabled}
          />
        );
      },
    });

    baseColumns.push({
      header: 'Ghi chú rule',
      formatter: (_, index) => (
        <FormInput
          bordered
          disabled={disabled}
          field={`procurement_rule_list.[${index}].note`}
          placeholder='Ví dụ: áp dụng cho hóa đơn tháng 4'
        />
      ),
    });

    return baseColumns;
  }, [disabled, procurementDiscountTypeCode]);

  const actions = useMemo(
    () => [
      {
        globalAction: true,
        icon: ICON_COMMON.add,
        type: 'success',
        content: 'Thêm dòng',
        hidden: disabled || !procurementDiscountTypeCode || SINGLE_RULE_TYPES.includes(procurementDiscountTypeCode),
        onClick: handleAddRule,
      },
      {
        icon: ICON_COMMON.trash,
        color: 'red',
        hidden: disabled || !procurementDiscountTypeCode || procurementRuleList.length <= 1,
        onClick: (_, index) => handleDeleteRule(index),
      },
    ],
    [disabled, procurementDiscountTypeCode, procurementRuleList.length],
  );

  return (
    <BWAccordion isRequired title='Điều kiện chiết khấu procurement'>
      <ProcurementHintBox title='Nguyên tắc dựng rule chiết khấu'>
        Chọn loại chiết khấu ở phần trên, sau đó khai báo rule bên dưới. Hệ thống sẽ dùng chính rule này để tự áp vào
        báo giá NCC trong hồ sơ duyệt giá, tính ra giá sau chiết khấu và chuyển tiếp cho kế toán khi quyết toán.
      </ProcurementHintBox>

      <div className='bw_frm_box bw_mt_1'>
        <div className='bw_col_12 bw_mb_2'>
          <div className='bw_text_secondary'>
            <b>Loại chiết khấu đang chọn:</b>{' '}
            {ProcurementDiscountTypeOptions.find((item) => item.value === procurementDiscountTypeCode)?.label || '--'}
          </div>
          {!!procurementDiscountTypeCode && (
            <div className='bw_text_secondary bw_mt_1'>{RULE_TYPE_HINTS[procurementDiscountTypeCode]}</div>
          )}
        </div>

        {!!procurementDiscountTypeCode && (
          <div className='bw_col_12'>
            <DataTable
              hiddenActionRow
              noPaging
              noSelect
              loading={loading}
              data={procurementRuleList}
              columns={columns}
              actions={actions}
            />
          </div>
        )}

        <div className='bw_col_12 bw_mt_2'>
          <label className='bw_checkbox bw_col_12'>
            <FormInput disabled={disabled} field='is_apply_all_discount_program' type='checkbox' />
            <span></span> Cho phép cộng dồn với chương trình chiết khấu khác
          </label>
        </div>

        <FormInput
          hidden
          disabled={disabled}
          type='text'
          field='check_procurement_rule_list'
          style={{ lineHeight: 1, display: 'none' }}
          validation={{
            validate: () => {
              if (!procurementDiscountTypeCode) {
                return 'Loại chiết khấu procurement là bắt buộc';
              }

              if (!procurementRuleList?.length) {
                return 'Điều kiện chiết khấu procurement là bắt buộc';
              }

              return true;
            },
          }}
        />
      </div>
    </BWAccordion>
  );
};

DiscountProgramCondition.propTypes = {
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default DiscountProgramCondition;
